import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { getScrollableAncestors } from "../../helpers/getScrollableAncestors";
import { isElement } from "../../helpers/isElement";
import {
  constrainHorizontalAlignPosition,
  constrainVerticalAlignPosition,
} from "./helpers/constrainAlignPosition";
import { constrainSidePosition } from "./helpers/constrainSidePosition";
import { getPosition } from "./helpers/getPosition";
import { Align, Side } from "./types";

export type GetPositionArgs = {
  align: Align;
  alignOffset: number;
  anchorRef: React.RefObject<HTMLElement | null>;
  contentRef: React.RefObject<HTMLElement | null>;
  offset: number;
  side: Side;
  viewportMargin: number;
};

export type Position = {
  left: number;
  top: number;
};

export type UseRelativePositionArg = {
  align: Align;
  alignOffset: number;
  anchorRef: RefObject<HTMLElement | null>;
  close: () => void;
  contentRef: React.RefObject<HTMLElement | null>;
  offset: number;
  open: boolean;
  side: Side;
  viewportMargin: number;
};

export function useRelativePosition({
  align,
  alignOffset,
  anchorRef,
  close,
  contentRef,
  offset,
  open,
  side,
  viewportMargin,
}: UseRelativePositionArg) {
  const [position, setPosition] = useState<
    ({ side?: Side | null } & Position) | null
  >(null);
  const rafRef = useRef<number | null>(null);

  const updatePosition = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (!anchorRef.current || !contentRef?.current || !open) return;
      const positionArgs: GetPositionArgs = {
        align,
        alignOffset,
        anchorRef,
        contentRef,
        offset,
        side,
        viewportMargin,
      };
      const nextPosition = getPosition(positionArgs);
      const constrainedSide = constrainSidePosition(nextPosition, positionArgs);

      const alignConstrainFunction =
        side === "top" || side === "bottom"
          ? constrainHorizontalAlignPosition
          : constrainVerticalAlignPosition;

      const constrainedAlign = alignConstrainFunction(
        constrainedSide?.position ?? null,
        positionArgs,
      );
      setPosition(
        constrainedAlign
          ? { ...constrainedAlign, side: constrainedSide?.side }
          : null,
      );
    });
  }, [
    align,
    side,
    contentRef,
    anchorRef,
    open,
    viewportMargin,
    offset,
    alignOffset,
  ]);

  useEffect(() => {
    if (!contentRef?.current || !anchorRef.current) return;
    updatePosition();

    // MutationObserver for DOM changes inside content
    const mutationObserver = new MutationObserver(updatePosition);
    mutationObserver.observe(contentRef.current, {
      characterData: true,
      childList: true,
      subtree: true,
    });

    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(contentRef.current);
    resizeObserver.observe(anchorRef.current);

    const controller = new AbortController();
    const eventOptions = { passive: true, signal: controller.signal };
    getScrollableTargets(anchorRef.current)?.forEach((t) =>
      t.addEventListener("scroll", close, eventOptions),
    );
    window.addEventListener("scroll", close, eventOptions);
    window.addEventListener("resize", updatePosition, eventOptions);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      controller.abort();
    };
  }, [updatePosition, close, contentRef, anchorRef, open]);

  useEffect(() => {
    if (!open) {
      const timeoutId = setTimeout(() => setPosition(null), 100);
      return () => clearTimeout(timeoutId);
    }
    updatePosition();
  }, [open, updatePosition]);

  return { position };
}

const getScrollableTargets = (el: HTMLElement | null) => {
  if (!el) return;
  const scrollTargets = new Set<HTMLElement>();
  getScrollableAncestors(el).forEach((a) => scrollTargets.add(a));
  let node: Node | null = el.parentNode;
  while (isElement(node)) {
    scrollTargets.add(node);
    node = node.parentNode;
  }
  return scrollTargets;
};
